import { GroupEntity, stringifyEntityRef } from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { useEffect, useMemo, useState } from 'react';

/**
 * GitHubRepo_UserTeamsPicker
 *
 * A team dropdown scoped to the CURRENT user. It lists only the AD-backed
 * GitHub teams (catalog `kind: Group`, `spec.type: team`) that the person
 * launching the template is a member of, instead of every team in the catalog
 * (which is what the built-in `OwnerPicker` shows).
 *
 * How it works:
 *  1. `identityApi.getBackstageIdentity()` returns `ownershipEntityRefs` — the
 *     set of groups the signed-in user belongs to (direct + transitive).
 *  2. Those group refs are resolved via `catalogApi.getEntitiesByRefs` and
 *     filtered to AD-backed GitHub teams (`spec.type === 'team'`).
 *  3. The resulting teams are rendered in a Select.
 *
 * The stored value is the full entity ref (e.g. `group:default/<team-slug>`),
 * matching what `OwnerPicker` produced, so downstream templating that does
 * `values.repoOwner | parseEntityRef | pick('name')` (README, CODEOWNERS,
 * catalog-info owner) keeps working unchanged.
 */

type TeamOption = { ref: string; label: string };

const GROUP_TYPE_TEAM = 'team';

/**
 * AD-backed SSO control groups (e.g. `PFG-GITHUB-AD-IS-SSO`,
 * `PFG-GITHUB-AD-IS-SSO-CO`). Catalog group names are lowercase-hyphenated, so
 * this matches both `pfg-github-ad-<bu>-sso` and `pfg-github-ad-<bu>-sso-co`.
 * These are identity groups and must not be selectable as a catalog owner.
 */
const SSO_CONTROL_GROUP = /^pfg-github-ad-.*-sso(-co)?$/i;

export const UserTeamsPicker = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    formData,
    schema,
    uiSchema,
    idSchema,
  } = props;

  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();

  // Allow the template to override the group type filter if ever needed;
  // defaults to AD-backed GitHub teams (`spec.type: team`).
  const groupType =
    (uiSchema?.['ui:options']?.groupType as string | undefined) ??
    GROUP_TYPE_TEAM;

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setLoadError(undefined);
      try {
        const { ownershipEntityRefs = [] } =
          await identityApi.getBackstageIdentity();
        const groupRefs = ownershipEntityRefs.filter(ref =>
          ref.startsWith('group:'),
        );

        if (groupRefs.length === 0) {
          if (active) setTeams([]);
          return;
        }

        const { items } = await catalogApi.getEntitiesByRefs({
          entityRefs: groupRefs,
          fields: [
            'kind',
            'metadata.name',
            'metadata.namespace',
            'metadata.title',
            'spec.type',
          ],
        });

        const options = (items.filter(Boolean) as GroupEntity[])
          .filter(
            entity =>
              entity.kind === 'Group' &&
              String((entity.spec as { type?: string })?.type).toLowerCase() ===
                groupType.toLowerCase() &&
              // Exclude the broad org-wide catalog group; it is not a valid
              // component owner.
              entity.metadata.name.toLowerCase() !==
                'engineering_at_principal' &&
              // Exclude AD-backed SSO control groups (…-SSO, …-SSO-CO); they are
              // identity groups, not valid catalog owners.
              !SSO_CONTROL_GROUP.test(entity.metadata.name),
          )
          .map(entity => ({
            ref: stringifyEntityRef(entity),
            label: entity.metadata.title ?? entity.metadata.name,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        if (active) setTeams(options);
      } catch (err) {
        if (active) setLoadError((err as Error).message);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [identityApi, catalogApi, groupType]);

  const hasError = rawErrors !== undefined && rawErrors.length > 0 && !formData;
  const noTeams = !loading && !loadError && teams.length === 0;

  const helperText = useMemo(() => {
    if (loadError) return `Could not load your teams: ${loadError}`;
    if (noTeams)
      return 'You are not a member of any AD-backed GitHub team. Ask a team owner to add you, then reopen this form.';
    return schema?.description;
  }, [loadError, noTeams, schema?.description]);

  if (loading) {
    return <Progress />;
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={hasError || !!loadError}
      fullWidth
    >
      <InputLabel id={`${idSchema?.$id}-label`}>
        {schema?.title ?? 'Team'}
      </InputLabel>
      <Select
        labelId={`${idSchema?.$id}-label`}
        id={idSchema?.$id}
        value={formData ?? ''}
        disabled={noTeams || !!loadError}
        onChange={event =>
          onChange((event.target.value as string) || undefined)
        }
      >
        {teams.map(team => (
          <MenuItem key={team.ref} value={team.ref}>
            {team.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

/**
 * Ensures the submitted value is one of the current user's teams (the Select
 * already constrains this in the UI; this guards direct/programmatic input).
 */
export const validateUserTeams = () => {
  // No async catalog re-check here: the field only ever emits refs it rendered
  // from the user's own memberships, and `required` in the schema enforces a
  // selection. Kept as a hook point for future server-side hardening.
  return;
};
